import dayjs from "dayjs";
import { slotNameMap, slotTimeRanges } from "@/constants";
import { DatePattern, formatIsoDate } from "@/utils/date-format";
import type { RazaEventsCalendarEvent } from "./types";

type HallRef = {
  name?: string;
  shortCode?: string;
};

type BookingRef = {
  id?: string;
  organiser?: string;
  itsNo?: string;
};

function slotHours(slot: unknown): [number, number] {
  return slotTimeRanges[String(slot ?? "")] ?? [0, 1];
}

function slotLabel(slot: unknown): string {
  const key = String(slot ?? "");
  return slotNameMap[key] ?? key;
}

function eventTimes(date: unknown, slot: unknown): { start: Date; end: Date } | null {
  const ymd = formatIsoDate(date as string, { utc: true });
  if (!ymd) return null;
  const day = dayjs(ymd, DatePattern.ISO_DATE, true);
  if (!day.isValid()) return null;
  const [startHour, endHour] = slotHours(slot);
  const start = day.hour(startHour).minute(0).second(0).toDate();
  const end = day.hour(endHour).minute(0).second(0).toDate();
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return { start, end };
}

/**
 * Map hall-booking API rows onto raza calendar events for optional conflict overlay.
 * Ids are prefixed so they never collide with raza event ids.
 */
export function mapHallBookingsToRazaCalendarOverlay(rows: unknown[]): RazaEventsCalendarEvent[] {
  const out: RazaEventsCalendarEvent[] = [];
  for (const raw of rows as Record<string, unknown>[]) {
    const times = eventTimes(raw.date, raw.slot);
    if (!times) continue;

    const hall = raw.hall as HallRef | undefined;
    const booking = raw.booking as BookingRef | undefined;
    const blocked = Boolean(raw.isBlockedDate);
    const hallName = hall?.name?.trim() || hall?.shortCode?.trim() || "Hall";
    const purpose = typeof raw.purpose === "string" ? raw.purpose : undefined;
    const slot = slotLabel(raw.slot);
    const title = blocked ? `Blocked · ${hallName}` : hallName;
    const subTitle = [slot, purpose].filter(Boolean).join(" · ");
    const id = `hall-overlay:${String(raw.id)}`;

    out.push({
      id,
      title,
      subTitle,
      start: times.start,
      end: times.end,
      overlayKind: "hall",
      hallOverlay: {
        hallBookingId: raw.id as string | number,
        bookingId:
          (typeof raw.bookingId === "string" && raw.bookingId) ||
          (typeof booking?.id === "string" && booking.id) ||
          undefined,
        date: typeof raw.date === "string" ? raw.date : undefined,
        slot: typeof raw.slot === "string" ? raw.slot : undefined,
        purpose,
        isBlockedDate: blocked,
        hallName,
        organiser: booking?.organiser,
        itsNo: booking?.itsNo,
      },
      resource: {
        id,
        overlayKind: "hall",
        isBlockedDate: blocked,
      },
    });
  }
  return out;
}

export function parseHallOverlaySearchParam(raw: string | null): boolean {
  if (!raw) return false;
  const v = raw.trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes";
}
